import { ChevronUp, SkipBack, SkipForward } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { type ReviewSegment, type TranscriptSpeaker } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatTimecode } from "@/lib/format";
import { speakerColour, speakerName } from "@/lib/speakerColour";
import { useReviewPlayer } from "@/lib/useReviewPlayer";

/** The handoff's player: 96 bars, and a rate menu from 0.5× to 1.5×. */
const WAVE_BARS = 96;

const RATES = [0.5, 0.7, 0.8, 0.9, 1.0, 1.1, 1.2, 1.5];

export function PlayerBar({
  player,
  segments,
  speakers,
  wordStarts,
  duration,
  cutShortcut = false,
}: {
  player: ReturnType<typeof useReviewPlayer>;
  segments: ReviewSegment[];
  speakers: TranscriptSpeaker[];
  wordStarts: { startMs: number }[];
  duration: number | null;
  /** Whether S starts a new part here (SPCH-14's parts mode, while the draft can change). */
  cutShortcut?: boolean;
}) {
  const [rateOpen, setRateOpen] = useState(false);
  const track = useRef<HTMLDivElement>(null);

  const total = player.duration;

  /**
   * The 96 bars, and what they actually say.
   *
   * There is no waveform to draw: the audio is a file on a volume that may be four
   * gigabytes, and decoding it in the browser to find its amplitude is not a thing this
   * screen can do. So the bars are **speech density** — how many words were said in
   * each ninety-sixth of the recording — computed from timings that are already on the
   * page. It reads the way a waveform reads (silence is short, an argument is tall),
   * scrubs identically, and is true, which a decorative sine wave would not be.
   */
  const bars = useMemo(() => {
    const counts = new Array<number>(WAVE_BARS).fill(0);
    const span = (duration ?? 0) > 0 ? duration! : Math.max(1, wordStarts.at(-1)?.startMs ?? 1);

    for (const word of wordStarts) {
      const slot = Math.min(WAVE_BARS - 1, Math.max(0, Math.floor((word.startMs / span) * WAVE_BARS)));
      counts[slot] = (counts[slot] ?? 0) + 1;
    }

    const busiest = Math.max(1, ...counts);
    return counts.map((count) => 20 + (count / busiest) * 76);
  }, [duration, wordStarts]);

  const progress = total > 0 ? Math.min(1, player.time / total) : 0;

  const speaking = useMemo(() => {
    const ms = player.time * 1000;

    // The last segment that has started. Walked backwards rather than with
    // `findLastIndex`, which this build's lib target does not carry.
    let segment: ReviewSegment | undefined;
    for (let index = segments.length - 1; index >= 0; index--) {
      if ((segments[index]!.startsAtMs ?? 0) <= ms) {
        segment = segments[index];
        break;
      }
    }

    const position = speakers.findIndex((speaker) => speaker.id === segment?.speakerId);

    return position >= 0
      ? { name: speakerName(speakers[position]!, position), colour: speakerColour(position) }
      : { name: "Nobody yet", colour: "var(--text-muted)" };
  }, [player.time, segments, speakers]);

  const scrub = useCallback(
    (clientX: number) => {
      const rect = track.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return;

      player.seek(Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)) * total);
    },
    [player, total],
  );

  return (
    <div className="relative z-[3] flex shrink-0 flex-col gap-1.5 border-t border-[var(--border-subtle)] bg-[var(--surface-glass-heavy)] px-[var(--space-6)] pb-[var(--space-4)] pt-[var(--space-3)] backdrop-blur-[var(--blur-md)]">
      <div className="flex items-center gap-[var(--space-4)]">
        <span className="w-[44px] font-mono text-[length:var(--text-xs)] text-[var(--text-primary)]">
          {formatTimecode(player.time * 1000)}
        </span>

        <div
          ref={track}
          role="slider"
          tabIndex={0}
          aria-label="Position in the recording"
          aria-valuemin={0}
          aria-valuemax={Math.round(total)}
          aria-valuenow={Math.round(player.time)}
          aria-valuetext={formatTimecode(player.time * 1000)}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
              event.preventDefault();
              player.nudge(event.key === "ArrowLeft" ? -5 : 5);
            }
          }}
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            scrub(event.clientX);
          }}
          onPointerMove={(event) => {
            if (event.buttons === 1) scrub(event.clientX);
          }}
          className="flex h-[46px] flex-1 cursor-ew-resize select-none items-center gap-[2px]"
        >
          {bars.map((height, index) => {
            const played = index / WAVE_BARS <= progress;

            return (
              <div
                key={index}
                className="flex-1 rounded-[2px] transition-[background-color] duration-[120ms]"
                style={{
                  height: `${height}%`,
                  // Played bars run through the brand hue ramp, exactly as the handoff
                  // draws it; unplayed ones are a border token, so the track reads in
                  // both themes rather than only the dark one.
                  background: played
                    ? `oklch(0.72 0.15 ${Math.round(215 + 130 * (index / WAVE_BARS))})`
                    : "var(--border-strong)",
                }}
              />
            );
          })}
        </div>

        <span className="w-[44px] text-right font-mono text-[length:var(--text-xs)] text-[var(--text-muted)]">
          {formatTimecode(total * 1000)}
        </span>
      </div>

      <div className="flex items-center gap-[var(--space-4)]">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span
            aria-hidden
            className="h-[7px] w-[7px] shrink-0 rounded-full"
            style={{
              background: speaking.colour,
              animation: player.playing ? "omPulseDot 1.6s ease-in-out infinite" : undefined,
            }}
          />
          <span className="truncate text-[length:var(--text-xs)] text-[var(--text-secondary)]">{speaking.name}</span>
        </div>

        <div className="flex items-center justify-center gap-[var(--space-3)]">
          <button
            type="button"
            onClick={() => player.nudge(-5)}
            title="Back 5s (←)"
            aria-label="Back five seconds"
            className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-full border border-[var(--border-subtle)] text-[var(--text-secondary)] transition hover:border-[var(--border-strong)] hover:bg-[var(--surface-glass)] hover:text-[var(--text-primary)]"
          >
            <SkipBack size={15} strokeWidth={2} />
          </button>

          <button
            type="button"
            onClick={player.toggle}
            title="Play / pause (space)"
            aria-label={player.playing ? "Pause" : "Play"}
            className="relative inline-flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-[var(--vl-bridge)] text-[var(--text-inverse)] transition hover:brightness-110"
            style={{ animation: player.playing ? "omPlayGlow 2s ease-in-out infinite" : undefined }}
          >
            <span
              aria-hidden
              className="absolute -left-[55%] -top-[55%] h-[210%] w-[210%] rounded-full blur-[7px]"
              style={{
                background: "conic-gradient(from 0deg, var(--vl-bridge), var(--vl-accent-warm) 28%, var(--vl-life) 52%, var(--vl-accent-warm) 74%, var(--vl-bridge))",
                animation: "omSwirl 22s linear infinite",
                animationPlayState: player.playing ? "running" : "paused",
              }}
            />
            <span className="relative z-[1] inline-flex">
              {player.playing ? <PauseGlyph /> : <PlayGlyph />}
            </span>
          </button>

          <button
            type="button"
            onClick={() => player.nudge(5)}
            title="Forward 5s (→)"
            aria-label="Forward five seconds"
            className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-full border border-[var(--border-subtle)] text-[var(--text-secondary)] transition hover:border-[var(--border-strong)] hover:bg-[var(--surface-glass)] hover:text-[var(--text-primary)]"
          >
            <SkipForward size={15} strokeWidth={2} />
          </button>
        </div>

        <div className="flex min-w-0 flex-1 items-center justify-end gap-[var(--space-4)]">
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setRateOpen((open) => !open)}
              title="Playback speed"
              aria-expanded={rateOpen}
              className="inline-flex items-center gap-1 rounded-[var(--radius-full)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] py-1 pl-[9px] pr-[7px] font-mono text-[11px] text-[var(--text-secondary)] transition hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]"
            >
              {player.rate.toFixed(1)}×
              <ChevronUp size={11} strokeWidth={2} />
            </button>

            {rateOpen && (
              <div className="absolute bottom-[34px] right-0 z-[8] flex flex-col gap-px rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-3)] p-1 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.6)]">
                {RATES.map((rate) => (
                  <button
                    key={rate}
                    type="button"
                    onClick={() => {
                      player.setRate(rate);
                      setRateOpen(false);
                    }}
                    className={cn(
                      "rounded-[var(--radius-md)] px-3 py-1.5 text-right font-mono text-[11px] transition-colors hover:bg-[var(--surface-glass-md)] hover:text-[var(--text-primary)]",
                      Math.abs(rate - player.rate) < 0.001
                        ? "bg-[var(--surface-3)] text-[var(--text-primary)]"
                        : "text-[var(--text-muted)]",
                    )}
                  >
                    {rate.toFixed(1)}×
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {[
              { key: "space", label: "play" },
              { key: "←→", label: "5s" },
              { key: "F", label: "flag" },
              { key: "E", label: "edit" },
              ...(cutShortcut ? [{ key: "S", label: "new part" }] : []),
              // Not shortcuts, but they belong with them: the click is the one gesture on
              // this screen nobody would guess at and the one they will use most, and a
              // field somebody is typing in should say how to leave it finished.
              { key: "click", label: "fix a word" },
              { key: "enter", label: "save" },
            ].map((shortcut) => (
              <span
                key={shortcut.key}
                className="inline-flex items-center gap-1.5 text-[length:var(--text-2xs)] text-[var(--text-muted)]"
              >
                <kbd className="rounded-[4px] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--text-secondary)]">
                  {shortcut.key}
                </kbd>
                {shortcut.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Lucide's play and pause, inlined at 20 px so the swirl behind them stays a single
   stacking context. */
function PlayGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M6 4.5v15a1 1 0 0 0 1.53.85l12-7.5a1 1 0 0 0 0-1.7l-12-7.5A1 1 0 0 0 6 4.5Z" />
    </svg>
  );
}

function PauseGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}
